import { useState } from "react";
import type { Project } from "../api/types.js";

// 3.A.5.2 (vaihe 3.A, v1):
// "Liitä kansio tälle projektille" -modal. Vaihe 3.A v1: käyttäjä saa
// kopioitavan `.kratti-project.yaml`-sisällön joka liitetään kansioon
// käsin (esim. Finderissa avata kansio + uusi tiedosto).
// V2: daemon-event-flow joka kirjoittaa yamlin POST:n perusteella.

export type SidecarAttachModalProps = {
  project: Project;
  onClose: () => void;
};

export function SidecarAttachModal({ project, onClose }: SidecarAttachModalProps) {
  const [copied, setCopied] = useState(false);

  const yamlContent = generateYaml(project);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(yamlContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard ei tueta — text on edelleen valittavissa
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-lg border border-slate-700 bg-slate-900 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-100">
          Liitä kansio projektille &quot;{project.name}&quot;
        </h3>
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-slate-300">
          <li>Avaa Finder (tai terminal) kansiossa jonka haluat liittää tälle projektille.</li>
          <li>
            Luo polun juureen tiedosto nimeltä{" "}
            <code className="rounded bg-slate-800 px-1.5 py-0.5 text-cyan-300">
              .kratti-project.yaml
            </code>
          </li>
          <li>Kopioi alla oleva sisältö tiedostoon ja tallenna.</li>
          <li>
            Daemon havaitsee sidecar-tiedoston seuraavalla startup-scan:lla (kun
            daemon käynnistyy uudelleen tai manuaali-restart).
          </li>
        </ol>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-slate-400">
              .kratti-project.yaml
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="rounded bg-slate-700 px-3 py-1 text-xs font-medium text-slate-100 hover:bg-slate-600"
            >
              {copied ? "Kopioitu!" : "Kopioi"}
            </button>
          </div>
          <pre className="mt-2 overflow-auto rounded bg-slate-950 p-3 text-xs leading-relaxed text-slate-200">
            <code>{yamlContent}</code>
          </pre>
        </div>

        <p className="mt-4 text-xs text-slate-500">
          Multi-source-projekti: voit luoda saman tiedoston usean kansion juureen — daemon
          tunnistaa nuokin samaksi projektiksi (sama UUID).
        </p>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-white"
          >
            Sulje
          </button>
        </div>
      </div>
    </div>
  );
}

function generateYaml(p: Project): string {
  const lines: string[] = [
    `schemaVersion: 1`,
    `id: "${p.id}"`,
    `name: "${escapeYaml(p.name)}"`,
  ];
  if (p.description) lines.push(`description: "${escapeYaml(p.description)}"`);
  if (p.status) lines.push(`status: "${p.status}"`);
  return lines.join("\n") + "\n";
}

function escapeYaml(s: string): string {
  // Yksinkertainen escape: " ja \ — yaml-double-quoted-string-säännöt
  return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}
